import { Client, type Item } from "archipelago.js";

import { Player } from "@player";
import { ToastVariant } from "@enums";
import { AchievementEarnedEvents } from "../Achievements/Achievements";
import { Settings } from "../Settings/Settings";
import { SnackbarEvents } from "../ui/React/Snackbar";
import { EventEmitter } from "../utils/EventEmitter";
import { clearPendingLocations, recordSatisfiedLocations } from "./Checks";
import {
  AP_GAME,
  augmentationForItem,
  GOAL_ACHIEVEMENT,
  Goal,
  type GoalValue,
  parseGoal,
  programForItem,
} from "./Data";
import { grantAugmentation, grantProgram } from "./Grant";
import { selectUnappliedItems } from "./ItemIndex";

/**
 * How often to re-derive which locations are satisfied.
 *
 * Checks are found by sweeping game state rather than by hooking the many places that can satisfy
 * one. The interval only bounds how long a check waits before being recorded; it does not risk
 * losing one, since the sweep runs whether or not the client is connected.
 */
const CHECK_SWEEP_INTERVAL_MS = 5000;

export type ArchipelagoConnectionStatus = "Online" | "Offline" | "Reconnecting";

export const ArchipelagoConnectionEvents = new EventEmitter<[ArchipelagoConnectionStatus]>();

/**
 * Owns the Archipelago websocket connection.
 *
 * archipelago.js handles the connection handshake, the data package, and received-item indexing, but
 * it does not reconnect on its own, so the retry behavior here is modeled on Remote (the Remote File
 * API client), which is the game's only other websocket consumer.
 */
export class ArchipelagoConnection {
  private client = new Client();
  private unsubscribers: (() => void)[] = [];
  private reconnectTimeoutId?: number;
  private listening = false;
  private intentionallyClosed = false;
  /** Tracks whether a connection was ever established, so we do not spam identical error toasts. */
  private everConnected = false;
  /**
   * The goal this slot was generated with, read from slot data on login.
   *
   * Source Genesis is currently the only goal, so nothing reads this yet and isGoalComplete() does
   * not branch on it. Kept because it is where a second goal would be dispatched from.
   */
  private goal: GoalValue = Goal.SourceGenesis;
  /**
   * Whether a goal StatusUpdate was already sent on this connection.
   *
   * The server ignores repeats once a slot is marked goaled, so this is only to avoid pointless
   * packets. It deliberately does not persist: a fresh connection re-sends once, which is what
   * reports a goal that was completed while disconnected.
   */
  private goalSent = false;
  private sweepIntervalId?: number;

  public get status(): ArchipelagoConnectionStatus {
    if (this.client.authenticated) {
      return "Online";
    }
    return this.reconnectTimeoutId === undefined ? "Offline" : "Reconnecting";
  }

  public async connect(attempt = 1): Promise<void> {
    this.intentionallyClosed = false;

    // Subscribe before logging in: the server sends already-received items during the handshake.
    this.listen();

    const address = `${Settings.ArchipelagoAddress}:${Settings.ArchipelagoPort}`;
    const slotName = Settings.ArchipelagoSlotName;

    try {
      // Protocol is deliberately omitted: archipelago.js tries wss first and falls back to ws, so
      // this works against both a local server and a TLS-terminated hosted room.
      const slotData = await this.client.login(address, slotName, AP_GAME, {
        password: Settings.ArchipelagoPassword,
      });
      this.goal = parseGoal(slotData);
    } catch (error) {
      if (attempt <= 1 || this.everConnected) {
        SnackbarEvents.emit(`Archipelago: connection failed. ${String(error)}`, ToastVariant.ERROR, 5000);
      }
      this.scheduleReconnect(attempt + 1);
      return;
    }

    this.everConnected = true;
    SnackbarEvents.emit(`Archipelago: connected to ${address} as ${slotName}`, ToastVariant.SUCCESS, 3000);
    ArchipelagoConnectionEvents.emit("Online");

    this.resendAllChecks();
    this.checkGoal();
  }

  public disconnect(): void {
    this.intentionallyClosed = true;
    this.clearReconnect();
    this.unlisten();
    this.client.socket.disconnect();
    ArchipelagoConnectionEvents.emit("Offline");
  }

  private onItemsReceived = (items: Item[], startingIndex: number): void => {
    this.receiveItems(items, startingIndex);
  };

  private onDisconnected = (): void => {
    if (this.intentionallyClosed) {
      return;
    }
    SnackbarEvents.emit("Archipelago: connection lost.", ToastVariant.WARNING, 3000);
    this.scheduleReconnect(1);
  };

  /**
   * Attaches listeners exactly once. The client object is reused across reconnects, so re-attaching
   * on every attempt would grant each received item once per attempt.
   */
  private listen(): void {
    if (this.listening) {
      return;
    }
    this.listening = true;

    this.client.items.on("itemsReceived", this.onItemsReceived);
    this.client.socket.on("disconnected", this.onDisconnected);
    this.unsubscribers.push(
      // The periodic sweep would catch this anyway; subscribing just makes an achievement check
      // immediate rather than up to one interval late.
      AchievementEarnedEvents.subscribe(() => {
        this.sweepChecks();
        this.checkGoal();
      }),
    );

    // Runs regardless of connection state, so checks earned during an outage are still recorded.
    this.sweepIntervalId = window.setInterval(() => this.sweepChecks(), CHECK_SWEEP_INTERVAL_MS);
  }

  private unlisten(): void {
    if (!this.listening) {
      return;
    }
    this.listening = false;

    this.client.items.off("itemsReceived", this.onItemsReceived);
    this.client.socket.off("disconnected", this.onDisconnected);
    if (this.sweepIntervalId !== undefined) {
      window.clearInterval(this.sweepIntervalId);
      this.sweepIntervalId = undefined;
    }
    for (const unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.unsubscribers = [];
  }

  /**
   * Discards the stored item index when connecting to a different multiworld.
   *
   * An index only has meaning relative to the seed it was counted against; carrying one over to a new
   * seed would silently skip that seed's first items.
   *
   * Called from receiveItems() rather than only after login, because items can arrive during the
   * connection handshake. The seed name is safe to read by then: the server sends RoomInfo before any
   * ReceivedItems packet.
   */
  private syncSeed(): void {
    const seedName = this.client.room.seedName;
    if (seedName === "" || seedName === Player.archipelagoSeedName) {
      return;
    }

    if (Player.archipelagoSeedName !== "") {
      console.warn(
        `Archipelago: seed changed from "${Player.archipelagoSeedName}" to "${seedName}", resetting item index.`,
      );
    }
    Player.archipelagoSeedName = seedName;
    Player.archipelagoItemIndex = 0;
    // Checks are per-multiworld, so the previous seed's list means nothing here. The sweep will
    // rebuild whatever is still derivable from game state on its next pass.
    Player.archipelagoCheckedLocations = [];
    // Anything recorded from an event but not yet sent belonged to the old seed, and unlike a backdoor
    // it cannot be re-derived, so it must not leak into the new one.
    clearPendingLocations();
  }

  /**
   * Applies newly received items.
   *
   * Archipelago resends every item ever received on each reconnect, so this is called with items the
   * player already has. Each item carries an absolute index; anything below the stored index has
   * already been applied and is skipped. This is what makes repeatable items safe — grantAugmentation()
   * is also idempotent, but that only protects items which cannot stack.
   *
   * The index advances even for items that are unmapped or that fail to apply, so that one bad item
   * cannot permanently wedge the item stream.
   *
   * The updated index reaches disk on the next autosave, so a hard crash before then can replay the
   * most recent items.
   */
  private receiveItems(items: Item[], startingIndex: number): void {
    this.syncSeed();

    for (const { item, index } of selectUnappliedItems(items, startingIndex, Player.archipelagoItemIndex)) {
      try {
        const augmentation = augmentationForItem(item.name);
        const program = augmentation === undefined ? programForItem(item.name) : undefined;

        if (augmentation !== undefined) {
          grantAugmentation(augmentation);
        } else if (program !== undefined) {
          grantProgram(program);
        } else {
          console.warn(`Archipelago: received unmapped item "${item.name}".`);
        }
      } catch (error) {
        // One bad item must not tear down the connection.
        console.error(`Archipelago: failed to apply item "${item.name}".`, error);
      }
      Player.archipelagoItemIndex = index + 1;
    }

    this.checkGoal();
  }

  /**
   * Reports goal completion to the server, once, when the chosen goal is met.
   *
   * Called after receiving items, after earning an achievement, and on connect. The connect call is
   * what covers a goal reached while the client was disconnected.
   */
  private checkGoal(): void {
    if (this.goalSent || !this.client.authenticated || !this.isGoalComplete()) {
      return;
    }

    try {
      this.client.goal();
      this.goalSent = true;
      SnackbarEvents.emit("Archipelago: goal complete!", ToastVariant.SUCCESS, 5000);
    } catch (error) {
      console.error("Archipelago: failed to report goal completion.", error);
    }
  }

  private isGoalComplete(): boolean {
    return Player.achievements.some((achievement) => achievement.ID === GOAL_ACHIEVEMENT);
  }

  /**
   * Records any newly satisfied locations and sends them.
   *
   * Recording happens even when disconnected, which is the point: the sweep runs on a timer, so a
   * backdoor installed during an outage is captured before the next augmentation install wipes it
   * from game state.
   *
   * Also reachable from Player.giveAchievement() via AchievementEarnedEvents, which can run during a
   * React render, so this must never throw and must not touch React state synchronously.
   */
  private sweepChecks(): void {
    let newlyChecked: string[];
    try {
      newlyChecked = recordSatisfiedLocations();
    } catch (error) {
      console.error("Archipelago: failed to collect satisfied locations.", error);
      return;
    }

    for (const locationName of newlyChecked) {
      this.sendLocationCheck(locationName);
    }
  }

  private sendLocationCheck(locationName: string): void {
    if (!this.client.authenticated) {
      // Not lost: the location is already in Player.archipelagoCheckedLocations, and resendAllChecks
      // replays the whole list on the next successful connection.
      return;
    }

    try {
      const locationId = this.client.package.findPackage(AP_GAME)?.locationTable[locationName];
      if (locationId === undefined) {
        // Expected for servers the apworld has no location for, such as purchased servers.
        return;
      }
      this.client.check(locationId);
    } catch (error) {
      console.error(`Archipelago: failed to check location "${locationName}".`, error);
    }
  }

  /**
   * Replays every check this save has ever satisfied.
   *
   * LocationChecks is idempotent server-side, so re-sending costs nothing and removes any need to
   * track which of them the server has already seen. Sweeping first picks up anything that became
   * true while the client was disconnected, or before it was ever configured.
   */
  private resendAllChecks(): void {
    this.sweepChecks();

    for (const locationName of Player.archipelagoCheckedLocations) {
      this.sendLocationCheck(locationName);
    }
  }

  private scheduleReconnect(attempt: number): void {
    this.clearReconnect();

    // A delay of 0 disables automatic reconnection, matching the Remote File API convention.
    if (Settings.ArchipelagoReconnectionDelay <= 0) {
      ArchipelagoConnectionEvents.emit("Offline");
      return;
    }

    this.reconnectTimeoutId = window.setTimeout(() => {
      this.reconnectTimeoutId = undefined;
      void this.connect(attempt);
    }, Settings.ArchipelagoReconnectionDelay * 1000);

    ArchipelagoConnectionEvents.emit("Reconnecting");
  }

  private clearReconnect(): void {
    if (this.reconnectTimeoutId !== undefined) {
      window.clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = undefined;
    }
  }
}
